import React, { useCallback, useEffect, useState, useContext } from "react";
import useDebounce from "@root/hooks/useDebounce";
import getSearchResultProducts from "@root/components/header/multisearch-category";
import Icon from "@root/_components/Icon/icon";
import categoryIcons from "../category-search-icons.json";
import "./mobile-search.scss";
import lockBodyScroll from "@helpers/lockBodyScroll";
import { trackSearchResults } from "@root/analytics";
import { SearchContext } from "context/search";

const initialShowMoreState = {
    suggestions: 6,
    categories: 6,
    products: 6,
};

const MobileSearch = ({ onClose }) => {
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState({ products: [], categories: [] });
    const [emptyResults, setEmptyResults] = useState({ products: [], categories: [] });
    const [showMore, setShowMore] = useState({ ...initialShowMoreState });
    const [isEmpty, setIsEmpty] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const debouncedQuery = useDebounce(query, 200);
    const debouncedTrackingQuery = useDebounce(query, 1000);
    const inputRef = React.useRef(null);
    const scrollRef = React.useRef(null);
    const { triggerPosition } = useContext(SearchContext);

    const handleSearch = useCallback(async (event) => {
        setQuery(event.target.value);
    }, []);
    const { inputValue } = useContext(SearchContext);
    useEffect(() => {
        setQuery(inputValue);
    }, [inputValue]);

    const handleClear = useCallback(() => {
        setQuery("");
    }, []);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [inputRef]);

    const handleClose = useCallback(() => {
        setIsAnimating(false);
        setTimeout(() => {
            onClose();
        }, 400);
    }, [onClose]);

    useEffect(() => {
        async function doQuery() {
            setLoading(true);
            const res = await getSearchResultProducts(debouncedQuery);

            if (res.emptyUserQuery) {
                setEmptyResults(res);
            }

            if (res.products.length === 0 && res.categories.length === 0) {
                setResults(emptyResults);
                setIsEmpty(true);
            } else {
                setResults(res);
                setIsEmpty(false);
            }

            setLoading(false);
            setShowMore({ ...initialShowMoreState });

            if (scrollRef.current) {
                scrollRef.current.scrollTo({
                    top: 0,
                });
            }
        }
        doQuery();

    }, [debouncedQuery]);

    useEffect(() => {
        trackSearchResults(debouncedTrackingQuery);
    }, [debouncedTrackingQuery]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsAnimating(true);
        }, 10);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (triggerPosition) {
            const element = document.querySelector('.mobile-search');
            if (element) {
                const containerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ub-em-container-height')) || 0;
                const adjustedTop = triggerPosition.top - containerHeight;

                element.style.setProperty('--trigger-top', `${triggerPosition.top}px`);
                element.style.setProperty('--trigger-left', `${triggerPosition.left}px`);
                element.style.setProperty('--trigger-width', `${triggerPosition.width}px`);
                element.style.setProperty('--trigger-height', `${triggerPosition.height}px`);
                element.style.setProperty('--adjusted-top', `${adjustedTop}px`);

                element.style.transformOrigin = `${triggerPosition.left}px ${adjustedTop}px`;
            }
        }
    }, [triggerPosition]);

    function listTypeProducts(returnAll = false) {
        const products = results.products.filter(product => product.group === 'list');
        if (returnAll) {
            return products;
        }
        return products.slice(0, showMore.suggestions);
    }

    function gridTypeProducts(returnAll = false) {
        const products = results.products.filter(product => product.group === 'grid');
        if (returnAll) {
            return products;
        }
        return products.slice(0, showMore.products);
    }

    function categories(returnAll = false) {
        const categories = results.categories;
        if (returnAll) {
            return categories;
        }
        return categories.slice(0, showMore.categories);
    }

    /**
     * Toggle show all vs 6
     * @param type
     */
    function showMoreToggle(type) {
        let scrollTop = scrollRef.current.scrollTop;

        setShowMore((prevState) => ({
            ...prevState,
            [type]: prevState[type] === 6 ? 300 : 6,
        }));

        setTimeout(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTo({
                    top: scrollTop,
                });
            }
        }, 0);
    }

    return (
        <div className={`mobile-search ${gridTypeProducts().length > 0 ? "mobile-search--has-products" : ""} ${!results.emptyUserQuery ? "mobile-search--has-query" : ""} ${isAnimating ? "mobile-search--open" : ""}`}>
            <div className="mobile-search__header">
                <div className="mobile-search__input-wrapper">
                    <Icon icon="search" color="#B3B3B3" size={24} />
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search..."
                        className="mobile-search__input"
                        onChange={handleSearch}
                        value={query}
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="mobile-search__clear"
                            aria-label="Clear search"
                        >
                            clear
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleClose}
                    className="mobile-search__close"
                    aria-label="Close search"
                >
                    <Icon icon="x" color="#B3B3B3" size={24} />
                </button>
            </div>
            <div className="mobile-search__results" ref={scrollRef}>

                {isEmpty && (
                    <div className="mobile-search__results-empty">
                        <h3>Sorry! We couldn’t find any results.</h3>

                        <p>Try using different keywords or check out our trending categories below.</p>
                    </div>
                )}


                {listTypeProducts().length > 0 && (
                    <div className="mobile-search__section mobile-search__section--list" id="section--suggestions">
                        <div className="mobile-search__title">
                            {!results.emptyUserQuery ? "Suggestions" : "Trending Searches"}
                        </div>
                        <div className="mobile-search__list">
                            {listTypeProducts().map((product) => (
                                <a key={product.internalId} className="mobile-search__item" href={product.redirect_url}>
                                    <div className="mobile-search__item-content">
                                        <div
                                            className="mobile-search__item-title"
                                            dangerouslySetInnerHTML={{ __html: product.displayTitle }}
                                        />
                                    </div>
                                </a>
                            ))}
                        </div>

                        {!results.emptyUserQuery && listTypeProducts(true).length > 6 && (
                            <button
                                type="button"
                                id="show-more--suggestions"
                                className="mobile-search__show-more"
                                onClick={() => showMoreToggle("suggestions")}
                            >
                                {showMore.suggestions === 6 ? `Show ${listTypeProducts(true).length - 6} more` : "Show less"}
                            </button>
                        )}
                    </div>
                )}
                {categories().length > 0 && (
                    <div className="mobile-search__section mobile-search__section--categories" id="section--categories">
                        <div className="mobile-search__title">
                            {!results.emptyUserQuery ? "Categories" : "Trending Categories"}
                        </div>
                        <div className="mobile-search__categories-grid">
                            {categories().map((category) => {
                                const firstTwoWords = category.displayTitle.split(" ").slice(0, 2).join(" ");
                                const iconKey = Object.keys(categoryIcons).find((key) => key.toLowerCase().includes(firstTwoWords.toLowerCase()));
                                const iconPath = iconKey ? categoryIcons[iconKey] : null;
                                return (
                                    <a
                                        key={category.internalId}
                                        className="mobile-search__category-item"
                                        href={category.category_redirect_url}
                                    >
                                        {iconPath && (
                                            <img
                                                src={iconPath}
                                                alt=""
                                                className="category-icon"
                                            />
                                        )}
                                        <span className="category-title"
                                            dangerouslySetInnerHTML={{ __html: category.displayTitle }} />
                                    </a>
                                );
                            })}
                        </div>
                        {!results.emptyUserQuery && categories(true).length > 6 && (
                            <button
                                type="button"
                                id="show-more--categories"
                                className="mobile-search__show-more"
                                onClick={() => showMoreToggle("categories")}
                            >
                                {showMore.categories === 6 ? `Show ${categories(true).length - 6} more` : "Show less"}
                            </button>
                        )}
                    </div>
                )}
                {gridTypeProducts().length > 0 && (
                    // me if trigger e kqyrem a o prej patios, nqofse o prej patios me i qit qato trending products tparat
                    <div className={`mobile-search__section mobile-search__section--products ${!(triggerPosition === null || !results.emptyUserQuery) ? "mobile-search__section--first" : ""}`} id="section--products">
                        <div className="mobile-search__title">
                            {!results.emptyUserQuery ? "Products" : "Trending Products"}
                        </div>
                        <div className="mobile-search__products-grid">
                            {gridTypeProducts().map((product) => (
                                <a
                                    key={`${product.master_product_id}-${product.id}`}
                                    className="mobile-search__product-item"
                                    href={`/products/${product.handle}`}
                                >
                                    <div className="product-image__wrapper">
                                        <img
                                            className="product-image"
                                            src={product.image}
                                            alt={product.title}
                                        />
                                    </div>
                                    <div
                                        className="product-title"
                                        dangerouslySetInnerHTML={{ __html: product.displayTitle }}
                                    />
                                </a>
                            ))}
                        </div>
                        {!results.emptyUserQuery && gridTypeProducts(true).length > 6 && (
                            <button
                                type="button"
                                id="show-more--products"
                                className="mobile-search__show-more"
                                onClick={() => showMoreToggle("products")}
                            >
                                {showMore.products === 6 ? `Show ${gridTypeProducts(true).length - 6} more` : "Show less"}
                            </button>
                        )}
                    </div>
                )}

                <div className={`mobile-search__spacer`}></div>

                <div className="mobile-search__results-footer">
                    <div className="mobile-search__results-footer-inner">
                        <div className="mobile-search__results-footer-title">
                            Can’t find what you’re looking for?
                        </div>
                        <div className="mobile-search__results-footer-button">
                            <a href="tel:8009150038" className="button button--small">Call 800-915-0038</a>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default MobileSearch; 